import { useMemo, useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { format } from 'date-fns';
import { styled } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Button from '@components/Button';
import { ModalBase } from '@components/Modal';
import { Chip } from '@components/Chip';
import { FiltersFormCreator } from '@components/FiltersFormCreator';
import { FiltersIcon } from 'assets/images/icons';
import { updateState, getChipsLabel, getKeywordsFilterOptions, createUpdatedChips } from '@helpers/filters';
import { FiltersIntegrationType } from '@constants/integrations';
import { useTranslation, useLocations } from '@hooks';

const Modal = styled(ModalBase)({
    '& .modal-base-paper': {
        width: 564,
        '@media (max-width: 1024px)': {
            width: '100%',
            height: '100%',
            borderRadius: 0,
            maxHeight: 'none',
        },
    },
    '& .modal-base-content': {
        padding: 16,
    },
});

export const FiltersWrapper = styled('div')({
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    rowGap: '8px',
    '@media print': {
        '& button': {
            display: 'none',
        },
    },
});

const FiltersPanel = ({
    className,
    setFiltersOpened,
    filtersNumber,
    filterOpened,
    onApplyFilters,
    disabled = false,
    keywords = [],
    paramsKeyword,
    filter,
    setFilter,
    withChips = true,
    type,
    setFilterNumber,
}) => {
    const firstRender = useRef(true);
    const { t } = useTranslation();
    const [filterChips, setFilterChips] = useState({
        companies: [],
        integrations: [],
        keywords: [],
        rating: [],
        timeframe: {},
    });
    const [refetchFilters, setRefetchFilters] = useState(false);

    const history = useHistory();
    const isMobile = useMediaQuery('@media (max-width: 768px)');
    const { getLocationsOptions, locationsMap } = useLocations();

    useEffect(() => {
        if (refetchFilters) {
            setRefetchFilters(false);
            onApplyFilters();
        }
    }, [refetchFilters]);

    useEffect(() => {
        if (firstRender.current && filter.companies.length > 0) {
            applyChips();
            firstRender.current = false;
        }
    }, [filter]);

    const options = useMemo(() => {
        //locationsOptions
        const filterCb = l => l?.profiles?.length > 0 && l?.profiles.some(p => p.type in FiltersIntegrationType);
        const locationsOptions = getLocationsOptions(filterCb);
        //integrationsOptions
        const integrationsOptions = Array.from(
            new Set(
                filter.companies
                    .map(item => locationsMap[item.id]?.profiles || [])
                    .flat()
                    .filter(p => p.type in FiltersIntegrationType)
                    .map(p => p.type),
            ),
        ).map(type => ({ value: type, label: FiltersIntegrationType[type] }));

        //keywordsOptions
        let keywordsOptions = [];
        if (!filter.companies.length || Object.keys(keywords).length === 0) {
            keywordsOptions = [];
        } else {
            keywordsOptions = getKeywordsFilterOptions(
                keywords,
                filter.companies.map(c => c.id),
            );
        }

        //ratingOptions
        const ratingOptions = [
            { value: 1, label: '1' },
            { value: 2, label: '2' },
            { value: 3, label: '3' },
            { value: 4, label: '4' },
            { value: 5, label: '5' },
        ];

        return {
            companies: locationsOptions,
            integrations: integrationsOptions,
            keywords: keywordsOptions,
            rating: ratingOptions,
        };
    }, [filter, locationsMap]);

    useEffect(() => {
        const allFilters = { ...filter, ...filterChips };
        const filterNumber = Object.keys(allFilters).reduce((total, key) => {
            let increment = 0;

            switch (key) {
                case 'timeframe':
                    increment = filterChips[key]?.start ? 1 : 0;
                    break;
                case 'withoutReplies':
                    increment = filter[key] ? 1 : 0;
                    break;
                default:
                    increment = filterChips[key]?.length > 0 ? 1 : 0;
                    break;
            }

            return total + increment;
        }, 0);

        setFilterNumber(filterNumber);
    }, [filterChips, filter]);

    const updateCompanySensitiveData = newCompaniesIds => {
        const integrations = [
            ...new Set(
                newCompaniesIds
                    .map(selectedItem => locationsMap[selectedItem])
                    .flatMap(location => location?.profiles)
                    .filter(p => p.type in FiltersIntegrationType)
                    .map(p => p.type),
            ),
        ];

        const keywordsIds = getKeywordsFilterOptions(keywords, newCompaniesIds).map(kw => kw.value);

        return { integrations, keywordsIds };
    };

    const update = {
        companies: items => {
            const selectedValues = items.length ? items : [];
            const selectedValuesIds = selectedValues.length ? selectedValues.map(item => item.id) : [];
            const isAllIntegrationsSelected = filter.integrations.length === options.integrations.length;
            const isAllKeywordsSelected = filter.keywords.length === options.keywords.length;

            const { integrations, keywordsIds } = updateCompanySensitiveData(selectedValuesIds);

            setFilter(prev => ({
                ...prev,
                companies: selectedValues,
                integrations: updateState(prev?.integrations, integrations, isAllIntegrationsSelected),
                keywords: updateState(prev?.keywords, keywordsIds, isAllKeywordsSelected),
            }));
        },
        integrations: value => setFilter(prev => ({ ...prev, integrations: value })),
        keywords: value => {
            setFilter(prev => ({
                ...prev,
                keywords: value,
                ignoreKeywords: value.length === options.keywords.length || !options.keywords.length || !value.length,
            }));
            if (paramsKeyword) {
                history.replace();
            }
        },
        rating: value => setFilter(prev => ({ ...prev, rating: value })),
        timeframe: data => {
            setFilter(prev => ({ ...prev, timeframe: { start: data[0], end: data[1] } }));
        },
        withoutReplies: e => setFilter(prev => ({ ...prev, withoutReplies: e.target.checked })),
    };

    const CHIPS_ACTIONS = {
        companies: {
            onDelete: () => {
                update.companies(options.companies);
                setFilterChips(prev => ({ ...prev, companies: [] }));
            },
            value: getChipsLabel(filterChips?.companies),
        },
        integrations: {
            onDelete: () => {
                setFilter(prev => ({
                    ...prev,
                    integrations: updateCompanySensitiveData(filter?.companies?.map(c => c.id)).integrations,
                }));
                setFilterChips(prev => ({ ...prev, integrations: [] }));
            },
            value: getChipsLabel(filterChips?.integrations),
        },
        keywords: {
            onDelete: () => {
                setFilter(prev => ({
                    ...prev,
                    keywords: updateCompanySensitiveData(filter?.companies?.map(c => c.id)).keywordsIds,
                    ignoreKeywords: true,
                }));
                setFilterChips(prev => ({ ...prev, keywords: [] }));
                history.replace();
            },
            value: getChipsLabel(filterChips?.keywords),
        },
        rating: {
            onDelete: () => {
                setFilter(prev => ({ ...prev, rating: [1, 2, 3, 4, 5] }));
                setFilterChips(prev => ({ ...prev, rating: [] }));
            },
            value: getChipsLabel(filterChips?.rating),
        },
        timeframe: filterChips?.timeframe?.start
            ? {
                  onDelete: () => {
                      setFilter(prev => ({ ...prev, timeframe: { start: null, end: null } }));
                      setFilterChips(prev => ({ ...prev, timeframe: { start: null, end: null } }));
                  },
                  value: `${format(new Date(filterChips.timeframe.start), 'dd.MM.yyyy')} - ${format(
                      new Date(filterChips.timeframe.end),
                      'dd.MM.yyyy',
                  )}`,
              }
            : { onDelete: () => {}, value: '' },
    };

    const applyChips = () => {
        const updatedChips = createUpdatedChips(filterChips, filter, options);
        setFilterChips(updatedChips);
    };

    const createFormOptions = (filter, update, options) => {
        const formOptions = [];

        for (let key in filter) {
            if (key === 'ignoreKeywords' || key === 'reviewId') continue;

            if (Array.isArray(filter[key])) {
                formOptions.push({
                    type: key === 'companies' ? 'autocomplete' : 'multiSelect',
                    label: t(`filters.${key}`),
                    value: filter[key],
                    onChange: update[key],
                    options: options[key],
                    countable: true,
                    error: !filter[key].length && key !== 'keywords', // INFO: keywords is not required
                });
            } else if (key === 'withoutReplies') {
                formOptions.push({
                    type: 'switch',
                    label: t(`filters.${key}`),
                    value: filter[key],
                    onChange: update[key],
                });
            } else if (key === 'timeframe') {
                formOptions.push({
                    type: 'timeframe',
                    label: t(`filters.${key}`),
                    value: [filter[key].start, filter[key].end],
                    onChange: update[key],
                });
            }
        }

        return formOptions;
    };

    const isValid = Object.keys(filter).every(key => {
        if (key === 'keywords') {
            return true;
        } else if (Array.isArray(filter[key])) {
            return !!filter[key].length;
        } else {
            return true;
        }
    });

    return (
        <FiltersWrapper>
            <Button
                className={className}
                variant="outlined"
                onClick={() => setFiltersOpened(true)}
                startIcon={<FiltersIcon />}
                disabled={disabled}
                sx={{ marginRight: '8px' }}
            >
                {t('buttons.filters')}
                {filtersNumber > 0 ? ` (${filtersNumber})` : ''}
            </Button>
            {withChips &&
                !isMobile &&
                Object.keys(filterChips).map(
                    key =>
                        (!!filterChips[key]?.length || filterChips[key]?.start) && (
                            <Chip
                                key={key}
                                label={CHIPS_ACTIONS[key].value}
                                onDelete={() => {
                                    CHIPS_ACTIONS[key].onDelete();
                                    setRefetchFilters(true);
                                }}
                            />
                        ),
                )}
            <Modal
                isOpen={filterOpened}
                handleClose={() => setFiltersOpened(false)}
                title={t('buttons.filters')}
                onPrimaryAction={() => {
                    applyChips();
                    setRefetchFilters(true);
                    setFiltersOpened(false);
                }}
                primaryActionDisabled={!isValid}
                primaryActionText={t('buttons.applyFilters')}
                onSecondaryAction={() => setFiltersOpened(false)}
                secondaryActionText={t('buttons.cancel')}
            >
                <FiltersFormCreator type={type} options={createFormOptions(filter, update, options)} />
            </Modal>
        </FiltersWrapper>
    );
};

export default FiltersPanel;
